import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, CheckCircle, Sparkles, Clock, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StreetAutocomplete } from "@/components/address/StreetAutocomplete";
import { useCityAutofill } from "@/lib/address";

function formatMMSS(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Multi-Vendor Kombi-Checkout aus dem Warenkorb.
 * Owner-Regel (2026-07-05): EINE Zahlung für alle Artikel, System splittet
 * intern in N Orders (eine pro Seller, komplette Order — kein Payout-Split).
 * Escrow-Logik bleibt intakt: PAYMENT_CONFIRMED löst nur Shipping/Notifications
 * aus. Seller-Payout kommt weiterhin über Modul A nach Delivery-Confirm
 * (STX/MP) bzw. sofort via mystery_payout_splits (Wundertüten/Rescue).
 */
export default function CartCheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const area = searchParams.get("area");  // "MARKETPLACE" | "STILLTOUCHEDX" | null

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [instructions, setInstructions] = useState(null);
  // iter139: Reservation-Lock State
  const [lockInfo, setLockInfo] = useState(null); // {locked: [...], lock_duration_seconds}
  const [remainingSec, setRemainingSec] = useState(null);
  const [lockError, setLockError] = useState(null);
  const releasedRef = useRef(false);

  // billing / shipping form state — prefill from user profile
  const [payMethod, setPayMethod] = useState("BANK_TRANSFER");
  const [buyerName, setBuyerName] = useState("");
  const [billStreet, setBillStreet] = useState("");
  const [billHouseNr, setBillHouseNr] = useState("");
  const [billZip, setBillZip] = useState("");
  const [billCity, setBillCity] = useState("");

  // iter272 · Phase 2 · Persönliche Daten (Profil) + abweichende Lieferadresse
  const [profileData, setProfileData] = useState(null);
  const [personalComplete, setPersonalComplete] = useState(null); // null=laden
  const [shipDifferent, setShipDifferent] = useState(false);
  const [shipName, setShipName] = useState("");
  const [shipStreet, setShipStreet] = useState("");
  const [shipHouseNr, setShipHouseNr] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [shipCity, setShipCity] = useState("");

  useEffect(() => {
    if (!user) { navigate("/login?next=/cart/checkout"); return; }
    (async () => {
      try {
        const initBody = area ? { area } : {};
        const { data } = await api.post("/cart/checkout-init", initBody);
        setCart(data);
        // iter139: Phase-2 Lock anfordern
        try {
          const { data: lk } = await api.post("/cart/checkout-lock", { area: area || undefined });
          setLockInfo(lk);
          const nowMs = Date.now();
          const earliest = (lk.locked || []).reduce((min, l) => {
            const t = new Date(l.expires_at).getTime();
            return min === null || t < min ? t : min;
          }, null);
          if (earliest) setRemainingSec(Math.max(0, Math.floor((earliest - nowMs) / 1000)));
          else setRemainingSec(null);
        } catch (le) {
          if (le.response?.status === 409) {
            const d = le.response.data?.detail || {};
            setLockError(d.message || t("cart_checkout.lock_conflict", {
              defaultValue: "Ein Artikel wird gerade von einem anderen Käufer reserviert. Bitte später erneut versuchen.",
            }));
          } else {
            // andere Fehler ignorieren → Checkout darf trotzdem laufen (kein Unikat vorhanden)
          }
        }
      } catch (e) {
        if (e.response?.status === 409) {
          alert(t("cart_checkout.cart_empty"));
          navigate("/cart");
          return;
        }
        alert(e.response?.data?.detail || t("cart_checkout.load_failed"));
      } finally {
        setLoading(false);
      }
      const p = user.profile || {};
      setBuyerName("");
      setBillStreet(p.address_street || "");
      setBillHouseNr(p.address_house_number || "");
      setBillZip(p.address_zip || "");
      setBillCity(p.address_city || "");
      // iter272 · Phase 2 · Verbindliche persönliche Daten aus dem Profil laden.
      try {
        const { data: prof } = await api.get("/profile");
        setProfileData(prof);
        const kn = `${prof.first_name || ""} ${prof.last_name || ""}`.trim();
        setBuyerName(kn);
        setBillStreet(prof.address_street || "");
        setBillHouseNr(prof.address_house_number || "");
        setBillZip(prof.address_zip || "");
        setBillCity(prof.address_city || "");
      } catch (e) { /* noop */ }
      try {
        const { data: acc } = await api.get("/profile/access");
        setPersonalComplete(!!acc.personal_data_complete);
      } catch (e) { setPersonalComplete(true); }
    })();
  }, [user, navigate, area, t]);

  // iter139: 1-Sekunden-Tick für Countdown
  useEffect(() => {
    if (remainingSec === null) return undefined;
    const iv = setInterval(() => {
      setRemainingSec((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    return () => clearInterval(iv);
  }, [remainingSec]);

  // iter139: Beim Verlassen der Seite Locks releasen (falls noch nicht bezahlt)
  useEffect(() => {
    const releaseIfNeeded = () => {
      if (releasedRef.current) return;
      if (session) return; // Zahlung bereits gestartet → Backend markiert später COMPLETED
      releasedRef.current = true;
      // Sync-Request nicht möglich; sendBeacon-Alternative
      try {
        api.post("/cart/checkout-lock/release").catch(() => {});
      } catch (e) { /* noop */ }
    };
    window.addEventListener("beforeunload", releaseIfNeeded);
    return () => {
      window.removeEventListener("beforeunload", releaseIfNeeded);
      releaseIfNeeded();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const shipOk = !shipDifferent || (shipName && shipStreet && shipHouseNr && shipZip && shipCity);
  const shipCountry = profileData?.address_country || "DE";
  useCityAutofill(shipCountry, shipZip, shipCity, setShipCity);
  const canSubmit = personalComplete === true && shipOk && !submitting && remainingSec !== 0;

  const handlePay = async () => {
    setSubmitting(true);
    try {
      // Step 1: create session + N orders
      const { data: s } = await api.post("/cart/checkout", {
        payment_method: payMethod,
        area: area || undefined,
        shipping_name: shipDifferent ? shipName : buyerName,
        buyer_name: buyerName,
        guest_email: user.email,
        billing_street: billStreet,
        billing_house_number: billHouseNr,
        billing_zip: billZip,
        billing_city: billCity,
        billing_country: "DE",
        delivery_different: shipDifferent,
        delivery_mode: shipDifferent ? "DIFFERENT_ADDRESS" : "SAME_AS_ADDRESS",
        shipping_street: shipDifferent ? shipStreet : billStreet,
        shipping_house_number: shipDifferent ? shipHouseNr : billHouseNr,
        shipping_address: shipDifferent ? shipStreet : billStreet,
        shipping_zip: shipDifferent ? shipZip : billZip,
        shipping_city: shipDifferent ? shipCity : billCity,
        shipping_country: "DE",
      });
      setSession(s);
      // Step 2: init payment
      if (payMethod === "BANK_TRANSFER") {
        const { data: b } = await api.post("/payments/session/bank-transfer/create", {
          checkout_session_id: s.checkout_session_id,
        });
        setInstructions(b.instructions);
        window.dispatchEvent(new Event("cart-refresh"));
      } else if (payMethod === "PAYPAL") {
        const frontendUrl = window.location.origin;
        const { data: p } = await api.post("/payments/session/paypal/create", {
          checkout_session_id: s.checkout_session_id,
          frontend_url: frontendUrl,
        });
        if (p.approve_url) {
          window.location.href = p.approve_url;
        } else {
          alert(t("cart_checkout.paypal_url_missing"));
        }
      } else if (payMethod === "STRIPE") {
        // iter80: Stripe Hosted Checkout
        const originUrl = window.location.origin;
        const { data: p } = await api.post("/payments/session/stripe/create", {
          checkout_session_id: s.checkout_session_id,
          origin_url: originUrl,
        });
        if (p.url) {
          window.location.href = p.url;
        } else {
          alert(t("cart_checkout.stripe_url_missing"));
        }
      }
    } catch (e) {
      alert(e.response?.data?.detail?.message || e.response?.data?.detail || t("cart_checkout.checkout_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div data-testid="cart-checkout-loading" className="min-h-screen flex items-center justify-center"><Loader2 size={28} className="animate-spin text-white/60" /></div>;
  }

  // iter139: Lock-Konflikt → Blockier-Screen
  if (lockError) {
    return (
      <div data-testid="cart-checkout-lock-conflict" className="min-h-screen bg-white">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-[#E5E5E5]">
          <button onClick={() => navigate("/cart")} className="text-[#0A0A0A]" data-testid="cart-checkout-back-conflict"><ArrowLeft size={20} /></button>
          <h1 className="text-base font-bold">{t("cart_checkout.title_default", { defaultValue: "Zur Kasse" })}</h1>
        </div>
        <div className="mx-auto max-w-md px-4 py-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 border border-red-200 mb-4">
            <Lock size={28} className="text-red-600" />
          </div>
          <h2 className="text-lg font-bold mb-2">{t("cart_checkout.locked_title", { defaultValue: "Artikel gerade reserviert" })}</h2>
          <p className="text-sm text-[#666] mb-6">{lockError}</p>
          <button
            data-testid="cart-checkout-back-to-cart"
            onClick={() => navigate("/cart")}
            className="h-11 px-6 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">
            {t("cart_checkout.back_to_cart", { defaultValue: "Zurück zum Warenkorb" })}
          </button>
        </div>
      </div>
    );
  }

  // Success screen (Bank-Transfer instructions)
  if (session && instructions) {
    return (
      <div data-testid="cart-checkout-success" className="min-h-screen bg-white">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-[#E5E5E5]">
          <button onClick={() => navigate("/profile/purchases")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
          <h1 className="text-base font-bold">{t("cart_checkout.payment_pending")}</h1>
        </div>
        <div className="max-w-md lg:max-w-2xl mx-auto px-4 lg:px-6 py-6">
          <div className="flex items-center gap-2 mb-4 text-emerald-600">
            <CheckCircle size={20} /> <span className="font-semibold">{t("cart_checkout.orders_created", { count: session.order_count })}</span>
          </div>
          <div className="rounded-2xl border border-[#E5E5E5] p-4 bg-[#FAFAFA] space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("cart_checkout.bank_transfer_heading")}</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-[#999]">{t("cart_checkout.recipient")}</div><div className="col-span-2">{instructions.account_holder}</div>
              <div className="text-[#999]">{t("cart_checkout.iban")}</div><div className="col-span-2 font-mono">{instructions.iban}</div>
              <div className="text-[#999]">{t("cart_checkout.bic")}</div><div className="col-span-2 font-mono">{instructions.bic}</div>
              <div className="text-[#999]">{t("cart_checkout.amount")}</div><div className="col-span-2 font-bold">{instructions.amount} {instructions.currency}</div>
              <div className="text-[#999]">{t("cart_checkout.reference")}</div>
              <div data-testid="cart-checkout-reference" className="col-span-2 font-mono font-bold text-emerald-700">{instructions.reference}</div>
            </div>
            <p className="text-[10px] text-[#888] mt-2">{t("cart_checkout.deadline_hint", { days: instructions.payment_deadline_days, count: session.order_count })}</p>
          </div>
          <button
            data-testid="cart-checkout-view-orders"
            onClick={() => navigate("/profile/purchases")}
            className="mt-6 w-full h-11 rounded-full bg-[#0A0A0A] text-white font-medium">
            {t("cart_checkout.goto_purchases")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-checkout-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <div className="flex items-center gap-3 px-4 h-14 border-b border-[#E5E5E5]">
        <button onClick={() => navigate("/cart")} className="text-[#0A0A0A]" data-testid="cart-checkout-back"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold">{t("cart_checkout.title_combi", { count: cart?.available_count || 0 })}</h1>
      </div>

      <div className="mx-auto max-w-md lg:max-w-4xl px-4 lg:px-6 py-4 lg:py-6 space-y-6">
        {/* iter139: Reservations-Countdown-Banner */}
        {lockInfo && (lockInfo.locked || []).length > 0 && remainingSec !== null && (
          <div
            data-testid="cart-checkout-lock-timer"
            className={`rounded-2xl border p-3 flex items-center gap-3 ${remainingSec <= 60 ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
            <Clock size={18} className={remainingSec <= 60 ? "text-red-600" : "text-amber-600"} />
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-wider">
                {t("cart_checkout.reservation_active", { defaultValue: "Artikel für dich reserviert" })}
              </p>
              <p className="text-[11px] text-[#666]">
                {t("cart_checkout.reservation_body", {
                  count: (lockInfo.locked || []).length,
                  defaultValue: "{{count}} Unikat(e) werden für dich exklusiv gehalten. Bitte schliesse den Kauf innerhalb der Zeit ab.",
                })}
              </p>
            </div>
            <span data-testid="cart-checkout-countdown-value" className={`font-mono text-lg font-bold tabular-nums ${remainingSec <= 60 ? "text-red-700" : "text-amber-700"}`}>
              {formatMMSS(remainingSec)}
            </span>
          </div>
        )}
        {lockInfo && remainingSec === 0 && (
          <div data-testid="cart-checkout-lock-expired" className="rounded-2xl border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {t("cart_checkout.lock_expired", {
              defaultValue: "Reservierung abgelaufen. Bitte kehre zum Warenkorb zurück und starte den Checkout neu.",
            })}
          </div>
        )}

        {/* Cart summary */}
        <div className="rounded-2xl border border-[#E5E5E5] p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("cart_checkout.summary")}</p>
          {cart?.items?.map((it) => (
            <div key={it.cart_item_id} data-testid={`cco-item-${it.cart_item_id}`} className="flex items-center gap-2 text-xs">
              {it.source_type === "MYSTERY_RESERVATION" && <Sparkles size={11} className="text-amber-500" />}
              <div className="flex-1 truncate">{it.title}</div>
              <div className="font-mono">{Number(it.price_total).toFixed(2)} €</div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E5] mt-2">
            <span className="text-sm font-bold">{t("cart_checkout.total")}</span>
            <span data-testid="cco-total" className="text-lg font-bold">{Number(cart?.total_amount || 0).toFixed(2)} EUR</span>
          </div>
        </div>

        {/* iter272 · Phase 2 · Persönliche Daten aus dem Profil (verbindlich) */}
        <div className="rounded-2xl border border-[#E5E5E5] p-4 space-y-2" data-testid="cco-personal-data">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">
            {t("cart_checkout.billing_from_profile", { defaultValue: "Rechnungsadresse (aus deinem Profil)" })}
          </p>
          {personalComplete === false ? (
            <div data-testid="cco-personal-incomplete" className="rounded-xl border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-900">
                {t("cart_checkout.personal_incomplete_title", { defaultValue: "Persönliche Daten unvollständig" })}
              </p>
              <p className="text-[12px] text-amber-800 mt-1">
                {t("cart_checkout.personal_incomplete_body", { defaultValue: "Bitte vervollständige zuerst deinen Namen und deine Rechnungsadresse im Profil, bevor du den Kauf abschließen kannst." })}
              </p>
              <button
                data-testid="cco-goto-profile"
                onClick={() => navigate("/profile/edit")}
                className="mt-2 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-medium">
                {t("cart_checkout.complete_profile", { defaultValue: "Persönliche Daten vervollständigen" })}
              </button>
            </div>
          ) : (
            <div className="text-sm text-[#0A0A0A] leading-relaxed" data-testid="cco-billing-summary">
              <p className="font-medium">{buyerName || "—"}</p>
              <p>{billStreet} {billHouseNr}</p>
              <p>{billZip} {billCity}</p>
            </div>
          )}
        </div>

        {/* iter272 · Phase 2 · Abweichende Lieferadresse (nur physische Artikel) */}
        <div className="rounded-2xl border border-[#E5E5E5] p-4 space-y-2" data-testid="cco-delivery-block">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="cco-ship-different-toggle">
            <input type="checkbox" checked={shipDifferent} onChange={(e) => setShipDifferent(e.target.checked)} />
            <span className="text-sm font-medium">
              {t("cart_checkout.ship_different", { defaultValue: "Abweichende Lieferadresse angeben" })}
            </span>
          </label>
          {!shipDifferent ? (
            <p className="text-[11px] text-[#888]">
              {t("cart_checkout.ship_same_hint", { defaultValue: "Standard: Lieferung an die Rechnungsadresse." })}
            </p>
          ) : (
            <div className="space-y-2 pt-1" data-testid="cco-ship-fields">
              <input data-testid="cco-ship-name" placeholder={t("cart_checkout.name", { defaultValue: "Name" })} value={shipName} onChange={(e) => setShipName(e.target.value)} className="w-full rounded-full border border-[#E5E5E5] h-10 px-4 text-sm" />
              <div className="grid grid-cols-3 gap-2">
                <StreetAutocomplete
                  testId="cco-ship-street"
                  value={shipStreet}
                  onChange={setShipStreet}
                  country={shipCountry}
                  postalCode={shipZip}
                  locality={shipCity}
                  wrapperClassName="col-span-2"
                  className="w-full rounded-full border border-[#E5E5E5] h-10 px-4 text-sm"
                  placeholder={t("cart_checkout.street", { defaultValue: "Straße" })}
                />
                <input data-testid="cco-ship-hnr" placeholder={t("cart_checkout.house_nr", { defaultValue: "Nr." })} value={shipHouseNr} onChange={(e) => setShipHouseNr(e.target.value)} className="rounded-full border border-[#E5E5E5] h-10 px-4 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="cco-ship-zip" placeholder={t("cart_checkout.zip", { defaultValue: "PLZ" })} value={shipZip} onChange={(e) => setShipZip(e.target.value)} className="rounded-full border border-[#E5E5E5] h-10 px-4 text-sm" />
                <input data-testid="cco-ship-city" placeholder={t("cart_checkout.city", { defaultValue: "Stadt" })} value={shipCity} onChange={(e) => setShipCity(e.target.value)} className="col-span-2 rounded-full border border-[#E5E5E5] h-10 px-4 text-sm" />
              </div>
            </div>
          )}
        </div>

        {/* Payment method */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("cart_checkout.payment_method")}</p>
          {["BANK_TRANSFER", "PAYPAL", "STRIPE"].map((m) => (
            <label key={m} data-testid={`cco-payment-${m}`} className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer ${payMethod === m ? "border-[#0A0A0A] bg-[#F5F5F5]" : "border-[#E5E5E5]"}`}>
              <input type="radio" checked={payMethod === m} onChange={() => setPayMethod(m)} />
              <span className="text-sm font-medium">{m === "BANK_TRANSFER" ? t("cart_checkout.bank_transfer") : m === "PAYPAL" ? t("cart_checkout.paypal") : t("cart_checkout.stripe")}</span>
            </label>
          ))}
        </div>

        <button
          data-testid="cco-pay-btn"
          onClick={handlePay}
          disabled={!canSubmit}
          className="w-full h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-bold disabled:opacity-50 active:scale-[0.98]">
          {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("cart_checkout.pay_now", { amount: Number(cart?.total_amount || 0).toFixed(2) })}
        </button>
        <p className="text-[10px] text-[#888] text-center">
          {t("cart_checkout.escrow_note")}
        </p>
      </div>
    </div>
  );
}
